import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Upload } from "lucide-react";
import { beginIngestWatch, drw, errText, setLimits, uploadSet, uploadStatus, verifyThumbUrl } from "./api";
import RegionReread from "./RegionReread";
import IngestProgress from "./IngestProgress";
import LinkReview from "./LinkReview";
import { PagePreview, PageZoom } from "./PagePreview";

const MB = (n) => `${(n / (1024 * 1024)).toFixed(n > 10 * 1024 * 1024 ? 0 : 1)}MB`;

/** Upload a set (first set or a revision — the server matches by sheet number either way).
 *  DRW-FIX: chunked upload, then a background ingest whose per-step progress is polled. */
/* MARK-LINK-2 — the "which scope is each takeoff layer?" panel is GONE. Scope comes from the
 * markup's Subject and from nothing else, so there is nothing to ask about a layer. */

export default function UploadDialog({ resume, watching, projectId, guidance, hasSet, onDone, onClose }) {
  const [files, setFiles] = useState([]);
  const [limits, setLimits_] = useState(null);
  const [form, setForm] = useState({ source: hasSet ? "ops" : "precon_takeoff", discipline: "A", revision_label: "", issued_at: "", linked_addendum_ref: "",
                                     kind: "architectural", scopes: [], purpose: "" });
  const [shopOpts, setShopOpts] = useState(null);   // DRW-6 — this project's FRS scopes
  useEffect(() => { drw.get(`/projects/${projectId}/field-shop/options`).then(({ data }) => setShopOpts(data)).catch(() => setShopOpts({ scopes: [] })); }, [projectId]);
  const shop = form.kind === "field_shop";
  const shopInvalid = shop && (!form.scopes.length || !form.purpose);
  const [busy, setBusy] = useState(false);
  const [chunks, setChunks] = useState(null);
  const [job, setJob] = useState(null);
  const [verify, setVerify] = useState(null);     // FT-1 — { upload_id, pages: [...] } awaiting confirmation
  const [region, setRegion] = useState(false);    // DRW-5 §1 — region re-extraction panel
  const [picked, setPicked] = useState(new Set()); // pages ticked for a second title-block layout
  const [zoom, setZoom] = useState(null);          // SHEET-NUM-1 — the enlarged page, with zoom
  const [saving, setSaving] = useState(false);
  const [jobId, setJobId] = useState("");
  const poll = useRef(null);
  const [misses, setMisses] = useState(0);        // consecutive status checks that got no answer
  // DRW-LOAD-A1 — ONE poller, and nothing it does can reach the error overlay. A status check that
  // times out is retried quietly; only the job itself can report a failure.
  const watchJob = (id, onSettled) => {
    clearInterval(poll.current);
    openWatch();
    let soft = 0;
    poll.current = setInterval(async () => {
      try {
        const s = await uploadStatus(id);
        soft = 0;
        setMisses(0);
        watch.current?.renew?.();
        setJob(s);
        if (s.status === "verify" || s.status === "ready" || s.status === "failed") {
          clearInterval(poll.current);
          closeWatch();
          setBusy(false);
          await onSettled?.(s);
        }
      } catch {
        soft += 1;                               // work in progress is not an error
        setMisses(soft);
      }
    }, 1500);
  };
  // FLD-OFF-FIX-3 §2 — while the server is reading the PDF, no other request's failure may put
  // the app offline. The window opens with the first poll and closes when the job settles.
  const watch = useRef(null);
  const openWatch = () => { if (!watch.current) watch.current = beginIngestWatch(); };
  const closeWatch = () => { if (watch.current) { watch.current(); watch.current = null; } };

  // SHEET-NUM-1 — opened to finish a check that was left for later.
  useEffect(() => { if (resume?.upload_id) setVerify({ upload_id: resume.upload_id, pages: resume.pages || [] }); }, [resume]);

  // DRW-LOAD-A1 — opened again while the server is still working: pick the progress back up.
  useEffect(() => {
    if (!watching?.upload_id) return;
    setBusy(true);
    setJobId(watching.upload_id);
    watchJob(watching.upload_id, (s) => { if (s.status === "ready") onDone?.(s); });
    // eslint-disable-next-line
  }, [watching?.upload_id]);

  useEffect(() => {
    setLimits().then(setLimits_).catch(() => setLimits_({ max_bytes: 500 * 1024 * 1024, max_label: "500MB", max_parts: 20 }));
    return () => { clearInterval(poll.current); closeWatch(); };
  }, []);

  const cap = limits?.max_bytes || 500 * 1024 * 1024;
  const tooBig = files.filter((f) => f.size > cap);

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) { toast.error("Pick a PDF"); return; }
    if (tooBig.length) { toast.error(`${tooBig[0].name} is over ${limits?.max_label}`); return; }
    setBusy(true);
    try {
      const id = await uploadSet({ projectId, files, form, onProgress: setChunks });
      setJobId(id);
      watchJob(id, async (s) => {
        if (s.status === "verify") {
          try {
            const { data } = await drw.get(`/uploads/${id}/verify`);
            setVerify({ ...data, upload_id: id });
          } catch (e) { toast.error(errText(e, "Could not open the sheet numbers to check")); }
          return;
        }
        if (s.status === "ready") { onDone?.(s); if (s.form?.kind === "field_shop") onClose?.(); }
      });
    } catch (err) {
      setBusy(false);
      closeWatch();
      // §6 — a real message, never a silent spinner. `message` carries the hard-cap text.
      toast.error(err?.message && !err.response ? err.message : errText(err, "Upload failed"));
    }
  };

  const retryJob = async () => {
    const id = jobId || job?.id;
    if (!id) return;
    try {
      const { data } = await drw.post(`/uploads/${id}/retry`, {});
      setJob(data);
      setBusy(true);
      watchJob(id, (s) => { if (s.status === "ready") onDone?.(s); });
    } catch (e) { toast.error(errText(e, "Could not carry on from there")); }
  };

  const r = job?.status === "ready" ? job.report : null;
  const steps = job?.steps || [];
  const line = steps.filter((s) => s.status !== "waiting").map((s) => (
    s.status === "done" ? `${s.label.toLowerCase()} ✓`
      : s.status === "failed" ? `${s.label.toLowerCase()} failed`
        : s.total ? `${s.label.toLowerCase()} ${s.done}/${s.total}` : `${s.label.toLowerCase()}…`)).join(" · ");

  // FT-1 — the verification grid: a thumbnail per page next to the sheet number and title the read
  // proposed. Corrections here go in BEFORE the sheets are split, so the revision match uses the
  // number a human confirmed.
  const setPage = (i, patch) => setVerify((v) => ({ ...v, pages: v.pages.map((p, j) => (j === i ? { ...p, ...patch } : p)) }));
  const bulkPrefix = () => {
    const from = window.prompt("Replace what in every sheet number? (e.g. P)");
    if (from === null) return;
    const to = window.prompt(`Replace "${from}" with…`) ?? "";
    setVerify((v) => ({ ...v, pages: v.pages.map((p) => ({ ...p, sheet_number: p.sheet_number.split(from).join(to) })) }));
  };
  const confirmVerify = async () => {
    setSaving(true);
    try {
      const { data } = await drw.post(`/uploads/${verify.upload_id}/verify`, {
        pages: verify.pages.map((p) => ({ part_index: p.part_index, page_index: p.page_index,
                                          sheet_number: p.sheet_number, title: p.title })) });
      setVerify(null);
      setJob(data);
      setJobId(verify.upload_id);
      if (data.status === "ready") onDone?.(data);
      else {
        setBusy(true);
        // the split now runs on the server; this only watches it
        watchJob(verify.upload_id, (s) => { if (s.status === "ready") onDone?.(s); });
      }
    } catch (e) { toast.error(errText(e, "Could not confirm those sheets")); } finally { setSaving(false); }
  };

  if (verify) {
    // SHEET-NUM-1 — a page that came out of the Bluebeam page label is not a guess, so it is not
    // flagged. Only genuine trouble is: nothing read, a duplicate, or a failed read off the paper.
    const fromMeta = verify.pages.filter((p) => p.number_source === "metadata").length;
    const fromPage = verify.pages.length - fromMeta;
    const low = verify.pages.filter((p) => p.number_source !== "metadata" && p.read_confidence !== "high").length;
    const nums = verify.pages.map((p) => p.sheet_number.trim().toUpperCase());
    const dupes = new Set(nums.filter((n, i) => n && nums.indexOf(n) !== i));
    const isBad = (p) => !p.sheet_number.trim() || dupes.has(p.sheet_number.trim().toUpperCase())
      || (p.number_source !== "metadata" && p.number_confident === false);
    const flagged = verify.pages.filter(isBad).length;
    const pkey = (p) => `${p.part_index}-${p.page_index}`;
    const applyRegion = (rows) => setVerify((v) => ({ ...v, pages: v.pages.map((p) => {
      const r = rows.find((x) => x.part_index === p.part_index && x.page_index === p.page_index);
      return r ? { ...p, sheet_number: r.sheet_number || p.sheet_number, title: r.title || p.title,
                   read_confidence: "high", number_confident: true, number_source: "region",
                   was: { sheet_number: p.sheet_number, title: p.title } } : p;
    }) }));
    return (
      <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="upload-dialog">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col" data-testid="upload-verify">
          <div className="p-4 border-b flex items-center gap-2 flex-wrap">
            <Upload className="w-5 h-5 text-orange-600" />
            <div className="font-semibold">Check the sheet numbers</div>
            <span className="text-xs text-neutral-500" data-testid="verify-summary">
              {verify.pages.length} page(s) · <b data-testid="verify-from-metadata">{fromMeta}</b> from the Bluebeam page label
              {fromPage > 0 && <> · <b data-testid="verify-from-page">{fromPage}</b> read off the sheet</>}
              {low > 0 && <> · {low} need a look</>} · <b>{flagged}</b> flagged · nothing is filed until you confirm
            </span>
            <button type="button" onClick={() => setRegion((r) => !r)} className={`ml-auto text-xs px-2 min-h-9 rounded-md border ${region ? "bg-sky-700 text-white" : ""}`} data-testid="verify-region-toggle">Read by region…</button>
            <button type="button" onClick={bulkPrefix} className="text-xs underline" data-testid="verify-bulk">Fix all…</button>
            <div className="w-full flex items-center gap-2 text-[11px] text-neutral-500">
              <span>{picked.size ? `${picked.size} page(s) ticked — a re-read will only touch those` : "Tick pages to limit a re-read to them"}</span>
              <button type="button" onClick={() => setPicked(new Set(verify.pages.map(pkey)))} className="underline" data-testid="verify-select-all">Select all</button>
              <button type="button" onClick={() => setPicked(new Set())} className="underline" data-testid="verify-clear-all">Clear all</button>
            </div>
          </div>
          {region && <RegionReread uploadId={verify.upload_id} pages={verify.pages} selected={picked} onApply={applyRegion} onClose={() => setRegion(false)} />}
          <div className="flex-1 overflow-auto p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {verify.pages.map((p, i) => {
              const dupe = dupes.has(p.sheet_number.trim().toUpperCase());
              const blank = !p.sheet_number.trim();
              const meta = p.number_source === "metadata";
              const bad = isBad(p);
              return (
              <div key={pkey(p)} className={`flex gap-2 border rounded-lg p-2 ${bad ? "border-red-300 bg-red-50/40" : meta || p.read_confidence === "high" ? "border-neutral-200" : "border-amber-300 bg-amber-50/40"}`}
                   data-testid={`verify-page-${p.page_index}`} data-source={p.number_source || "page"}
                   data-flag={dupe ? "duplicate" : blank ? "blank" : bad ? "failed" : undefined}>
                <PagePreview uploadId={verify.upload_id} part={p.part_index} page={p.page_index} width={420}
                             label={`${p.sheet_number} ${p.title}`} className="w-40 sm:w-48 aspect-[4/3] shrink-0"
                             onExpand={() => setZoom({ part: p.part_index, page: p.page_index, title: `${p.sheet_number} · ${p.title}` })} />
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="text-[11px] text-neutral-500 flex items-center gap-2 flex-wrap">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={picked.has(pkey(p))} onChange={(e) => setPicked((s) => { const n = new Set(s); e.target.checked ? n.add(pkey(p)) : n.delete(pkey(p)); return n; })} data-testid={`verify-pick-${p.page_index}`} /> page {p.page_index + 1}</label>
                    {meta && <span className="rounded bg-emerald-50 text-emerald-800 px-1.5 py-0.5" data-testid={`verify-source-${p.page_index}`}>Bluebeam page label</span>}
                    {p.number_source === "region" && <span className="rounded bg-sky-50 text-sky-800 px-1.5 py-0.5" data-testid={`verify-source-${p.page_index}`}>re-read from your box</span>}
                    {!meta && p.number_source !== "region" && <span className="rounded bg-neutral-100 px-1.5 py-0.5" data-testid={`verify-source-${p.page_index}`}>read off the sheet</span>}
                    {dupe && <span className="text-red-700 font-semibold">duplicate number</span>}
                    {blank && <span className="text-red-700 font-semibold">no number</span>}
                    {!meta && !blank && p.number_confident === false && <span className="text-red-700 font-semibold">read failed</span>}
                    {!meta && !bad && p.read_confidence !== "high" && <span>low confidence read</span>}
                    {p.was && <span className="text-neutral-400">was {p.was.sheet_number || "—"}</span>}
                  </div>
                  <input value={p.sheet_number} onChange={(e) => setPage(i, { sheet_number: e.target.value })}
                         className="w-full h-9 border rounded-md px-2 font-mono text-sm" placeholder="Sheet number"
                         data-testid={`verify-number-${p.page_index}`} />
                  <input value={p.title} onChange={(e) => setPage(i, { title: e.target.value })}
                         className="w-full h-9 border rounded-md px-2 text-sm" placeholder="Sheet title"
                         data-testid={`verify-title-${p.page_index}`} />
                </div>
              </div>
              );
            })}
          </div>
          {zoom && <PageZoom uploadId={verify.upload_id} part={zoom.part} page={zoom.page} title={zoom.title} onClose={() => setZoom(null)} />}
          <div className="p-3 border-t flex items-center gap-2 justify-end">
            <button type="button" onClick={onClose} className="px-3 min-h-11 rounded-md border text-sm" data-testid="verify-cancel">Later</button>
            <button type="button" disabled={saving} onClick={confirmVerify}
                    className="px-4 min-h-11 rounded-md bg-orange-500 text-white text-sm font-semibold disabled:opacity-50"
                    data-testid="verify-confirm">{saving ? "Filing…" : "Confirm and file the set"}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="upload-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-auto">
        <div className="p-4 border-b flex items-center gap-2">
          <Upload className="w-5 h-5 text-orange-600" />
          <div className="font-semibold">{shop ? "Upload field shop drawings" : hasSet ? "Upload revised sheets (ASI / addendum)" : "Upload the drawing set"}</div>
          <button type="button" onClick={onClose} className="ml-auto text-neutral-400 hover:text-neutral-700" data-testid="upload-close">✕</button>
        </div>

        {!job && !busy && (
          <form onSubmit={submit} className="p-4 space-y-3 text-sm">
            <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-xs text-sky-900 space-y-1" data-testid="upload-guidance">
              <div className="font-semibold">Pre-Con input rule</div>
              {(guidance || []).map((g) => <div key={g}>• {g}</div>)}
            </div>
            <div className="rounded-lg bg-neutral-50 border p-2 text-[11px] text-neutral-600" data-testid="upload-limit-note">
              Up to <b>{limits?.max_label || "500MB"}</b> per PDF, uploaded in {limits ? MB(limits.chunk_size) : "5MB"} pieces so a weak
              link only ever resends one piece. A bigger set? Pick <b>several PDFs</b> — up to {limits?.max_parts || 20} — and they land as ONE set.
            </div>
            <input type="file" accept="application/pdf" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} className="block w-full text-sm" data-testid="upload-file" />
            {files.length > 0 && (
              <div className="text-xs space-y-0.5" data-testid="upload-file-list">
                {files.map((f, i) => (
                  <div key={f.name + i} className={`flex items-center gap-2 ${f.size > cap ? "text-red-700" : "text-neutral-600"}`}>
                    <span className="truncate">{files.length > 1 ? `Part ${i + 1}: ` : ""}{f.name}</span>
                    <span className="ml-auto tabular-nums">{MB(f.size)}</span>
                    {f.size > cap && <span className="font-semibold">over {limits?.max_label}</span>}
                  </div>
                ))}
              </div>
            )}
            <div className="rounded-lg border p-2 space-y-2" data-testid="upload-kind">
              <div className="text-xs font-semibold">Type <span className="font-normal text-neutral-500">— chosen here, never read from the file</span></div>
              <div className="grid grid-cols-2 gap-2">
                {[["architectural", "Architectural", "Sheets, takeoff marks, revisions"], ["field_shop", "Field shop drawings", "TC layouts, locker elevations, Inpro shop drawings — by room, no takeoffs"]].map(([k, l, d]) => (
                  <button key={k} type="button" onClick={() => setForm({ ...form, kind: k })} data-testid={`upload-kind-${k}`}
                          className={`text-left rounded-lg border p-2 ${form.kind === k ? "border-orange-500 bg-orange-50" : "bg-white"}`}>
                    <div className="font-semibold text-xs">{l}</div><div className="text-[11px] text-neutral-600">{d}</div>
                  </button>
                ))}
              </div>
              {shop && (
                <div className="space-y-2" data-testid="upload-shop-fields">
                  <div className="text-xs"><span className="font-semibold">Scope</span> <span className="text-neutral-500">— scopes on this project's FRS only; several = the union of their rooms</span></div>
                  <div className="flex flex-wrap gap-1">
                    {(shopOpts?.scopes || []).map((s) => (
                      <label key={s} className={`flex items-center gap-1 px-2 min-h-9 rounded-md border text-xs cursor-pointer ${form.scopes.includes(s) ? "bg-neutral-900 text-white" : "bg-white"}`} data-testid={`upload-shop-scope-${s}`}>
                        <input type="checkbox" className="sr-only" checked={form.scopes.includes(s)} onChange={(e) => setForm({ ...form, scopes: e.target.checked ? [...form.scopes, s] : form.scopes.filter((x) => x !== s) })} />{s}
                      </label>
                    ))}
                    {shopOpts && !shopOpts.scopes.length && <span className="text-xs text-red-700" data-testid="upload-shop-no-scopes">This project's FRS has no scopes yet — load the FRS first.</span>}
                  </div>
                  <div className="text-xs"><span className="font-semibold">Purpose</span> <span className="text-neutral-500">— required, no default</span></div>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setForm({ ...form, purpose: "FM" })} className={`min-h-11 rounded-lg border font-bold text-xs ${form.purpose === "FM" ? "bg-amber-500 text-black border-amber-600" : "bg-white"}`} data-testid="upload-shop-purpose-FM">FOR FM · field measurement</button>
                    <button type="button" onClick={() => setForm({ ...form, purpose: "INSTALL" })} className={`min-h-11 rounded-lg border font-bold text-xs ${form.purpose === "INSTALL" ? "bg-emerald-600 text-white border-emerald-700" : "bg-white"}`} data-testid="upload-shop-purpose-INSTALL">FOR INSTALL</button>
                  </div>
                  <div className="text-[11px] text-neutral-500">After upload you assign each sheet to its rooms; the set is not published until then. No sheet-number verification, no takeoffs, nothing to do with submittals.</div>
                </div>
              )}
            </div>
            {!shop && (
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs">Source
                <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1 w-full h-9 border rounded-md px-2" data-testid="upload-source">
                  <option value="precon_takeoff">Pre-Con takeoff</option><option value="ops">Ops</option><option value="field">Field</option><option value="gc">GC</option>
                </select>
              </label>
              <label className="text-xs">Discipline
                <input value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} className="mt-1 w-full h-9 border rounded-md px-2" data-testid="upload-discipline" />
              </label>
              <label className="text-xs">Revision label
                <input value={form.revision_label} onChange={(e) => setForm({ ...form, revision_label: e.target.value })} placeholder={hasSet ? "Rev 2 · ASI-01" : "Rev 1 · IFC"} className="mt-1 w-full h-9 border rounded-md px-2" data-testid="upload-revision-label" />
              </label>
              <label className="text-xs">Issued
                <input type="date" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} className="mt-1 w-full h-9 border rounded-md px-2" data-testid="upload-issued" />
              </label>
              <label className="text-xs col-span-2">Addendum / ASI reference (email or document)
                <input value={form.linked_addendum_ref} onChange={(e) => setForm({ ...form, linked_addendum_ref: e.target.value })} className="mt-1 w-full h-9 border rounded-md px-2" data-testid="upload-addendum-ref" />
              </label>
            </div>
            )}
            {shop && (
              <label className="text-xs block">Set name (e.g. "TC shop drawings · Inpro 09-30")
                <input value={form.revision_label} onChange={(e) => setForm({ ...form, revision_label: e.target.value })} placeholder="TC layouts" className="mt-1 w-full h-9 border rounded-md px-2" data-testid="upload-shop-label" />
              </label>
            )}
            {!shop && <div className="text-[11px] text-neutral-500">Sheets are matched by sheet number: matched → a new revision stacked over the prior one (markups carry), new numbers → new sheets, missing numbers → prior stays current, flagged "not reissued". Nothing is merged or deleted.</div>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-3 py-2 rounded-md border" data-testid="upload-cancel">Cancel</button>
              <button type="submit" disabled={!files.length || tooBig.length > 0 || shopInvalid} className="px-3 py-2 rounded-md bg-orange-500 text-white disabled:opacity-50" data-testid="upload-submit">{shop ? "Upload shop drawings" : "Upload & ingest"}</button>
            </div>
          </form>
        )}

        {(busy || (job && job.status !== "ready")) && (
          <div className="p-4 space-y-3 text-sm" data-testid="upload-progress">
            {!job && (
              <>
                <div className="h-2 rounded-full bg-neutral-200 overflow-hidden">
                  <div className="h-full bg-orange-500 transition-all" style={{ width: `${pct(chunks, steps)}%` }} data-testid="upload-progress-bar" />
                </div>
                <div className="text-xs text-neutral-700" data-testid="upload-progress-line">
                  {chunks ? `uploading ${chunks.done}/${chunks.total} piece${chunks.total === 1 ? "" : "s"}${chunks.phase === "retry" ? ` · retrying (${chunks.attempt})` : ""}` : "starting…"}
                </div>
              </>
            )}
            {job && <IngestProgress job={job} misses={misses} onRetry={job.status === "failed" ? retryJob : null} />}
            {job?.status === "failed" && (
              <div className="text-[11px] text-neutral-600" data-testid="upload-failed">
                The file is saved on the project and the set is kept, marked failed at this step. The previous revision is still the current one.
              </div>
            )}
            <div className="flex justify-end gap-2">
              {job?.status === "failed" && <button type="button" onClick={onClose} className="px-3 py-2 rounded-md border" data-testid="upload-failed-close">Close</button>}
            </div>
          </div>
        )}

        {r && (
          <div className="p-4 space-y-2 text-sm" data-testid="upload-report">
            {r.flattened && <div className="rounded-lg bg-red-50 border border-red-200 p-2 text-red-800 text-xs font-medium" data-testid="upload-flattened">Flattened — markups not readable; re-upload unflattened from Bluebeam Max.</div>}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span>New sheets</span><b data-testid="report-new">{r.new_sheets}</b>
              <span>Revised sheets</span><b data-testid="report-revised">{r.revised_sheets}</b>
              <span>Changed (clouds / raster)</span><b>{r.changed_sheets.join(", ") || "—"}</b>
              <span>Not reissued</span><b data-testid="report-not-reissued">{r.not_reissued.join(", ") || "—"}</b>
              <span>Takeoff marks read</span><b data-testid="report-takeoff-marks">{r.takeoff_marks}</b>
              <span>Markups carried</span><b>{r.carried}{r.verify ? ` (${r.verify} to verify)` : ""}</b>
              {r.frs_review_flagged && <><span>FRS</span><b className="text-orange-700">flagged for review</b></>}
            </div>
            {job.quantity_check?.rows?.length > 0 && (
              <div className="text-xs rounded-lg border bg-neutral-50 p-2 space-y-1" data-testid="report-counts">
                <div className="font-semibold">Takeoff and FRS, side by side</div>
                <div className="text-[11px] text-neutral-600">Two independent counts — the Bluebeam takeoff (by markup Subject) and the FRS. A difference is worth a look: a missed markup or a stale FRS. Neither count is treated as the right one, and no mark is matched to an FRS item here.</div>
                <table className="w-full">
                  <thead className="text-[10px] uppercase text-neutral-500"><tr><th className="text-left">Scope</th><th className="text-right">Takeoff marks</th><th className="text-right">FRS quantity</th><th className="text-right">Difference</th></tr></thead>
                  <tbody>
                    {job.quantity_check.rows.map((x) => (
                      <tr key={x.scope} data-testid={`report-count-${x.scope}`}>
                        <td className="font-mono">{x.scope}</td>
                        <td className="text-right tabular-nums">{x.takeoff_marks}</td>
                        <td className="text-right tabular-nums">{x.frs_quantity}</td>
                        <td className={`text-right tabular-nums ${x.gap ? "text-amber-700 font-semibold" : "text-neutral-400"}`}>{x.gap > 0 ? `+${x.gap}` : x.gap}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {job.quantity_check.unmapped_subjects?.length > 0 && (
                  <div className="text-[11px] text-neutral-600" data-testid="report-unmapped-subjects">Subjects that are not a scope on this job: {job.quantity_check.unmapped_subjects.map((l) => `${l.subject} (${l.marks})`).join(", ")} — answer them in the review below.</div>
                )}
              </div>
            )}
            {job.set_id && <LinkReview setId={job.set_id} />}
            <div className="flex justify-end"><button type="button" onClick={onClose} className="px-3 py-2 rounded-md bg-neutral-900 text-white" data-testid="upload-done">Open the sheets</button></div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Upload is the first third of the bar; the ingest steps share the rest. */
function pct(chunks, steps) {
  const up = chunks?.total ? (chunks.done / chunks.total) * 33 : 0;
  if (!steps.length) return Math.max(3, up);
  const sheets = steps.find((s) => s.key === "sheets");
  const doneSteps = steps.filter((s) => s.status === "done").length;
  const sheetPart = sheets?.total ? (sheets.done / sheets.total) * 45 : 0;
  return Math.min(100, Math.max(33 + sheetPart, (doneSteps / steps.length) * 100));
}
